/**
 * The icon (docs/design/visual-overhaul.md §7.3, T8, T9, R7 interim): one
 * glyph set (./icons.ts), three sizes plus two hero sizes, and a stroke per
 * size so 16px icons are not thin next to 600 headings.
 *
 *   s  16  inline with 12-14px text (chips, row status, message meta)
 *   m  20  every icon button, nav row, menu item and settings tab
 *   l  24  the rail and the voice bar
 *   xl 32, 2xl 40  empty states
 *
 * A sized icon sets its own box and stroke inline: no selector resizes it.
 * Without a size it is the medium one, sized by .nx-i (base.css) until each
 * area passes a size. Icon boxes are an even number of px larger than the
 * icon (T9), so the glyph lands on whole pixels.
 */
import { memo } from 'react';
import { ICONS, type IconName } from './icons';

export type IconSize = 's' | 'm' | 'l' | 'xl' | '2xl';

export const ICON_PX: Readonly<Record<IconSize, number>> = {
  s: 16,
  m: 20,
  l: 24,
  xl: 32,
  '2xl': 40,
};
/** Stroke in 24-unit viewBox units: 16 -> 2.25 (1.5 CSS px), 20 -> 2, 24 -> 2, heroes 1.75. */
export const ICON_STROKE: Readonly<Record<IconSize, number>> = {
  s: 2.25,
  m: 2,
  l: 2,
  xl: 1.75,
  '2xl': 1.75,
};

export interface IconProps {
  name: IconName;
  size?: IconSize;
  cls?: string;
}

export const Icon = memo(function Icon({ name, size, cls }: IconProps) {
  const c = 'nx-i' + (cls ? ' ' + cls : '');
  const px = size ? ICON_PX[size] : undefined;
  return (
    <svg
      className={c}
      // the glyph name: tests and the capture find items by it, whatever the language
      data-icon={name}
      data-size={size}
      viewBox="0 0 24 24"
      width={px}
      height={px}
      style={px ? { width: px, height: px } : undefined}
      fill="none"
      stroke="currentColor"
      strokeWidth={size ? ICON_STROKE[size] : ICON_STROKE.m}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      // static icon markup from our own set, never user content
      dangerouslySetInnerHTML={{ __html: ICONS[name] }}
    />
  );
});
